import { useCallback, useState, useEffect, type MouseEvent } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { runInAction } from "mobx";
import { Page } from "@boundary/layouts/pageLayout";
import { Button } from "@boundary/components/button";
import { useOrderFormState } from "@boundary/_hooks/useOrderFormState";
import { useFunnelStep } from "@boundary/_hooks/useFunnelStep";
import { AddServiceCard } from "@boundary/components/addServiceCard";
import { ServiceCard } from "@boundary/components/serviceCard";
import { CartSummary } from "@boundary/widgets/cartSummary";
import { PayWith } from "@boundary/components/payWith/payWith";
import { PromoCodeInput } from "@boundary/components/promoCodeInput";
import { prefetchAndNavigate } from "@boundary/utils/prefetchRoute";
import styles from "./cart.module.scss";

export const CartPage = observer(function CartPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const machineIdParam = searchParams.get("machineId");
  const machineId = machineIdParam ? Number(machineIdParam) : 0;
  
  const vm = useOrderFormState(machineId);
  const [isPromoApplying, setIsPromoApplying] = useState(false);
  const [isPromoFocused, setIsPromoFocused] = useState(false);

  useFunnelStep("cart_view");

  // Рассчитываем корзину с промокодом при загрузке и изменении услуг
  useEffect(() => {
    if (vm.services.length > 0) {
      void vm.calculateCartWithPromo();
    }
  }, [vm.services.length, vm.machineId]);

  const handleBack = useCallback((e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    prefetchAndNavigate(navigate, `/map?machineId=${machineId}`);
  }, [navigate, machineId]);

  const handleAddService = useCallback(() => {
    prefetchAndNavigate(navigate, `/catalog?machineId=${machineId}`);
  }, [machineId, navigate]);

  const handleEditService = useCallback((serviceId: string) => {
    const service = vm.services.find(s => s.id === serviceId);
    if (service) {
      runInAction(() => {
        vm.currentService = service;
        if (service.files.length > 0) {
          vm.currentFile = service.files[0];
        }
      });
      vm.saveState();
      prefetchAndNavigate(navigate, `/service?machineId=${machineId}&serviceType=${service.type}`);
    }
  }, [vm, machineId, navigate]);

  const handleDeleteService = useCallback((serviceId: string) => {
    const service = vm.services.find(s => s.id === serviceId);
    if (service) {
      vm.deleteService(service);
    }
  }, [vm]);

  const handleCheckout = useCallback(() => {
    if (vm.services.length === 0) return;
    prefetchAndNavigate(navigate, `/payment?machineId=${machineId}`);
  }, [vm.services.length, machineId, navigate]);

  const serviceSummaries = vm.services.map((service) => ({
    id: service.id,
    type: service.type,
    price: service.files.reduce(
      (acc, file) => acc + (vm.filePrice[file.id] ?? 0),
      0
    )
  }));

  return (
    <Page screenBackground="referralDomain">
      <Page.SafeArea />
      <Page.DisplayZone>
        <Page.DisplayZone.Header back onBackClick={handleBack} center={<h1 className={styles.headerTitle}>Корзина</h1>} />
        <Page.DisplayZone.Body className={styles.content}>
          <div className={styles.servicesGrid}>
            <AddServiceCard onClick={handleAddService} />
            {vm.services.map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
                onEdit={handleEditService}
                onDelete={handleDeleteService}
              />
            ))}
          </div>
        </Page.DisplayZone.Body>
        {vm.services.length > 0 && (
          <Page.DisplayZone.Sticky className={styles.buttonPad}>
            <PromoCodeInput 
              onCartRecalculate={() => vm.calculateCartWithPromo()} 
              onApplyingChange={setIsPromoApplying}
              onFocusChange={setIsPromoFocused}
            />
            {/* Скрываем только когда поле в фокусе И НЕ идёт применение */}
            {(!isPromoFocused || isPromoApplying) && (
              <>
                <PayWith
                  calculation={vm.cartCalculation}
                  payWith={vm.payWith}
                  onChange={(next) => vm.setPayWith(next)}
                  disabled={isPromoApplying}
                />
                <CartSummary
                  totalPrice={vm.totalPrice}
                  originalPrice={vm.cartCalculation?.originalPrice}
                  discount={vm.cartCalculation?.discount}
                  services={serviceSummaries}
                />
                <Button
                  onClick={handleCheckout}
                  disabled={vm.services.length === 0 || isPromoApplying}
                >
                  {isPromoApplying ? "Применяем промокод..." : vm.totalPrice === 0 ? "Оформить бесплатно" : "К оплате"}
                </Button>
              </>
            )}
          </Page.DisplayZone.Sticky>
        )}
      </Page.DisplayZone>
    </Page>
  );
});
